import { createClient } from '@supabase/supabase-js';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import type { Database } from './types';
import { resolveSupabaseConfig, supabaseBootLine } from './resolveConfig';

// SAB-852. The resolution -- including when a missing env is FATAL rather than a silent
// fall through to production -- lives in ./resolveConfig, which is pure and exhaustively
// unit-tested. Read the header there for why the fallback survives and what it protects.
//
// The two arguments are the whole contract:
//   import.meta.env   the three VITE_SUPABASE_* values, undefined when unset
//   second argument   whether the production fallback may apply
//
// `PROD` alone is NOT the right discriminator, which is worth stating because it is
// counter-intuitive. Measured from real bundles:
//
//   npm run build      MODE=production   DEV=false  PROD=true
//   npm run build:dev  MODE=development  DEV=false  PROD=true   <-- still true
//   vite (dev server)  MODE=development  DEV=true   PROD=false
//
// Vite ties DEV/PROD to "is this a build" (NODE_ENV), not to `--mode`, so `PROD` alone
// would let `npm run build:dev` fall back to production silently. Excluding the literal
// development mode makes a self-declared development artifact refuse the fallback, while
// a hypothetical `--mode staging` build still keeps it.
//
// Vite substitutes all of this at build time, so it is resolved in the bundle, not at runtime.
const config = resolveSupabaseConfig(
  import.meta.env,
  import.meta.env.PROD && import.meta.env.MODE !== 'development',
);

export const SUPABASE_PROJECT_ID = config.projectId;
export const SUPABASE_URL = config.url;
export const SUPABASE_PUBLISHABLE_KEY = config.publishableKey;

// Kept from the original file, and still reachable: resolveSupabaseConfig uses `??`, so an
// EXPLICIT-empty env value (VITE_SUPABASE_URL="") passes through as "" rather than being
// replaced by the fallback, and trips here. Unset is handled upstream; this is for empty.
if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    `[supabase/client] empty Supabase config — VITE_SUPABASE_URL=${JSON.stringify(SUPABASE_URL)}, VITE_SUPABASE_PUBLISHABLE_KEY=${SUPABASE_PUBLISHABLE_KEY ? '[set]' : '[empty]'}. Refusing to instantiate client.`
  );
}

// One line, at boot, naming the project REF -- not the URL, which is a template literal
// and so never appears as a literal string in the bundle. `source=fallback` in production
// is the signal that Vercel's env has been cleared and only the hardcode is holding it up.
console.info(supabaseBootLine(config));

// Auth session key used by Supabase. Derived from SUPABASE_PROJECT_ID so
// it auto-updates when env override flips the project ID.
const AUTH_KEY = `sb-${SUPABASE_PROJECT_ID}-auth-token`;

// TODO: [SECURITY — SAB-232] Mobile auth tokens stored in unencrypted Preferences.
// On iOS, @capacitor/preferences uses UserDefaults; on Android, SharedPreferences.
// Both are plaintext on disk. A jailbroken/rooted device, an `adb backup`, or
// any process running with the same UID can read the JWT.
// Migrate to @capacitor-community/secure-storage-plugin (iOS Keychain /
// Android EncryptedSharedPreferences) before NFL launch.
//
// SAB-561: Capacitor-safe, durable session storage (ASYNC).
//
// Why: the previous hybridStorage read ONLY localStorage synchronously and
// mirrored to Preferences fire-and-forget. On iOS that produced two session
// deaths: (1) WKWebView evicts localStorage on suspend/kill, so a cold boot read
// empty before the async Preferences seed landed; (2) a token rotation wrote the
// new token to localStorage sync but the async Preferences mirror could be lost
// if iOS killed the app first, so the next launch presented a burned refresh
// token ("Refresh Token Not Found").
//
// Now every op goes through @capacitor/preferences and is awaited by supabase-js:
//   - Native: Preferences -> iOS UserDefaults / Android SharedPreferences, which
//     survive localStorage eviction AND app suspension/kill. Preferences writes
//     the exact `CapacitorStorage.<AUTH_KEY>` UserDefaults key AppDelegate reads
//     for the FCM device-token attach, so that native contract is preserved.
//   - Web: @capacitor/preferences transparently falls back to localStorage, so
//     web login/refresh behavior is unchanged (still localStorage, just under the
//     `CapacitorStorage.` key prefix).
//
// Migration (do NOT log anyone out to fix logouts): the old adapter kept
// localStorage authoritative (sync) and Preferences a possibly-stale async
// mirror. On the FIRST auth read we reconcile by PREFERRING localStorage (so we
// never resurrect a pre-rotation token), copy it into Preferences, and set a
// one-time flag. setItem/removeItem also keep the legacy localStorage[AUTH_KEY]
// in sync so reverting this PR (pre-staged rollback) doesn't log users out.
// Exported for unit tests (client.storage.test.ts). AUTH_KEY is the supabase
// session key; MIGRATED_FLAG guards the one-time localStorage->Preferences reconcile.
export { AUTH_KEY };
export const MIGRATED_FLAG = `${AUTH_KEY}-cap-migrated`;

// SAB-572: NATIVE-ONLY in-memory cache + one-time migration check.
//
// Bug (pre-existing, from the SAB-561 async Preferences adapter): in the native
// WKWebView, supabase-js's auth refresh/recover machinery calls getItem(AUTH_KEY)
// very frequently, and EACH call hit the Capacitor bridge TWICE — Preferences.get
// (MIGRATED_FLAG) then Preferences.get (token). That produced a continuous
// dozens-per-second bridge storm (the alternating -cap-migrated / auth-token reads),
// which starved the JS event loop so downstream async work (acquireGeoSession's
// Locance gather) never got a turn → the SAB-572 location prompt never fired.
//
// Fix: on native, serve reads from an in-memory cache after the first hit and check
// the migration flag once per launch. Writes keep the cache coherent. This collapses
// the bridge traffic to ~one read per key per launch. WEB is left byte-for-byte
// unchanged (NATIVE gate) to avoid any multi-tab session-sync regression.
const NATIVE = Capacitor.isNativePlatform();
const memCache = new Map<string, string | null>();
let migrationChecked = false;

export const capacitorStorage = {
  getItem: async (key: string): Promise<string | null> => {
    if (NATIVE && memCache.has(key)) return memCache.get(key) ?? null;

    if (key === AUTH_KEY && !(NATIVE && migrationChecked)) {
      if (NATIVE) migrationChecked = true;
      const { value: migrated } = await Preferences.get({ key: MIGRATED_FLAG });
      if (!migrated) {
        // One-time reconcile: prefer the sync-written legacy localStorage value
        // over a possibly-stale Preferences mirror from the old hybridStorage.
        let legacy: string | null = null;
        try { legacy = localStorage.getItem(key); } catch { /* ignore */ }
        if (legacy != null) await Preferences.set({ key, value: legacy });
        await Preferences.set({ key: MIGRATED_FLAG, value: '1' });
      }
    }
    const { value } = await Preferences.get({ key });
    if (value != null) { if (NATIVE) memCache.set(key, value); return value; }
    // Post-migration fallback: Preferences empty but a legacy localStorage copy
    // still exists (e.g. web session written under the old key) -> adopt it.
    let legacy: string | null = null;
    try { legacy = localStorage.getItem(key); } catch { /* ignore */ }
    if (legacy != null) {
      await Preferences.set({ key, value: legacy });
      if (NATIVE) memCache.set(key, legacy);
      return legacy;
    }
    if (NATIVE) memCache.set(key, null);
    return null;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    if (NATIVE) memCache.set(key, value);
    await Preferences.set({ key, value });
    // Rollback-safety + web parity: mirror to the legacy localStorage key the old
    // hybridStorage read from, so a revert of this PR keeps existing sessions.
    try { localStorage.setItem(key, value); } catch { /* ignore */ }
  },
  removeItem: async (key: string): Promise<void> => {
    if (NATIVE) memCache.set(key, null);
    await Preferences.remove({ key });
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  },
};

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: capacitorStorage,
    persistSession: true,
    autoRefreshToken: true,
    // Build 6/7 native OAuth precursor: PKCE so signInWithOAuth returns a ?code=
    // plus a stored code_verifier we can exchange via exchangeCodeForSession on the
    // native appUrlOpen deep-link return. detectSessionInUrl (default true, set
    // explicitly here because PKCE correctness depends on it) auto-exchanges the
    // ?code= on web redirects, including OAuth, email confirmation, and recovery.
    // NOTE: PKCE also changes recovery + email-confirmation links from
    // #access_token to ?code=. Both keep working: ResetPassword listens for the
    // PASSWORD_RECOVERY onAuthStateChange event, and detectSessionInUrl handles
    // confirmation; the old hash-based detection simply goes unused.
    flowType: 'pkce',
    detectSessionInUrl: true,
  }
});